import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrand } from '@/config/brands';
import { familyStorage, FamilyMember } from '@/lib/familyStorage';
import type { LocalScenario } from '@/lib/scenarioStorage';

interface MonthlyOverlapHeatmapProps {
  members: FamilyMember[];
  memberScenarios: Record<string, LocalScenario[]>;
  timelineMonths: { date: Date; label: string; key: string }[];
}

function parseDurationToDays(duration: string): number {
  const match = duration.match(/(\d+)(?:\s*[-–]\s*(\d+))?\s*(day|week|month|year)/i);
  if (!match) return 30;
  const num = parseInt(match[2] || match[1]);
  const unit = match[3].toLowerCase();
  if (unit.startsWith('day')) return num;
  if (unit.startsWith('week')) return num * 7;
  if (unit.startsWith('month')) return num * 30;
  if (unit.startsWith('year')) return num * 365;
  return 30;
}

export function MonthlyOverlapHeatmap({ members, memberScenarios, timelineMonths }: MonthlyOverlapHeatmapProps) {
  const { t } = useTranslation('family');
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';

  const { grid, monthTotals, maxCost } = useMemo(() => {
    const grid: Record<string, Record<string, number>> = {};
    const monthTotals: Record<string, number> = {};
    let max = 0;

    for (const m of members) {
      grid[m.id] = {};
      for (const mInfo of timelineMonths) {
        grid[m.id][mInfo.key] = 0;
      }
    }
    for (const mInfo of timelineMonths) {
      monthTotals[mInfo.key] = 0;
    }

    for (const member of members) {
      const scenarios = memberScenarios[member.id] || [];
      for (const s of scenarios) {
        if (!s.plannedDate) continue;
        const start = new Date(s.plannedDate);
        const durationDays = Math.max(1, parseDurationToDays(s.duration || '1 month'));
        const end = new Date(start.getTime() + durationDays * 86400000);
        const costPerDay = (s.estimatedCost || 0) / durationDays;

        for (const mInfo of timelineMonths) {
          const [y, mo] = mInfo.key.split('-').map(Number);
          const monthStart = new Date(y, mo - 1, 1);
          const monthEnd = new Date(y, mo, 0);

          const overlapStart = Math.max(start.getTime(), monthStart.getTime());
          const overlapEnd = Math.min(end.getTime(), monthEnd.getTime());

          if (overlapStart <= overlapEnd) {
            const days = Math.ceil((overlapEnd - overlapStart) / 86400000) + 1;
            const cost = Math.round(costPerDay * days);
            grid[member.id][mInfo.key] += cost;
            monthTotals[mInfo.key] += cost;
            max = Math.max(max, grid[member.id][mInfo.key]);
          }
        }
      }
    }

    return { grid, monthTotals, maxCost: max };
  }, [members, memberScenarios, timelineMonths]);

  // Skip render if no data
  const hasData = Object.values(monthTotals).some(v => v > 0);
  if (!hasData) return null;

  function cellIntensity(cost: number): string {
    if (cost === 0 || maxCost === 0) return 'transparent';
    const ratio = Math.min(cost / maxCost, 1);
    if (isAhp) {
      const alpha = 0.1 + ratio * 0.8;
      return `rgba(0, 40, 104, ${alpha})`;
    }
    const alpha = 0.1 + ratio * 0.8;
    return `rgba(74, 222, 128, ${alpha})`;
  }

  function fmtCell(n: number): string {
    if (n === 0) return '';
    if (n >= 10000) return `${(n / 1000).toFixed(0)}k`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return `$${n}`;
  }

  return (
    <div className={`rounded-xl border p-4 ${
      isAhp ? 'border-gray-200 bg-white' : 'border-foreground/10 bg-card'
    }`}>
      <h3 className={`text-sm font-semibold mb-3 ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
        {t('plan.monthlyOverlap')}
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-[10px] border-collapse">
          <thead>
            <tr>
              <th className={`text-left py-1 px-2 font-medium ${isAhp ? 'text-gray-500' : 'text-foreground/50'}`}>
                {t('results.memberBreakdown', 'Member')}
              </th>
              {timelineMonths.map((m) => (
                <th key={m.key} className={`text-center py-1 px-1 font-medium min-w-[48px] ${
                  isAhp ? 'text-gray-500' : 'text-foreground/50'
                }`}>
                  {m.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map((member) => {
              const hasRow = Object.values(grid[member.id] || {}).some(v => v > 0);
              if (!hasRow) return null;

              return (
                <tr key={member.id}>
                  <td className={`py-1 px-2 font-medium whitespace-nowrap ${
                    isAhp ? 'text-gray-700' : 'text-foreground/70'
                  }`}>
                    {familyStorage.getDisplayName(member)}
                  </td>
                  {timelineMonths.map((mInfo) => {
                    const cost = grid[member.id]?.[mInfo.key] || 0;
                    return (
                      <td key={mInfo.key} className="py-1 px-1">
                        <div
                          className="rounded h-7 flex items-center justify-center font-mono transition-colors"
                          style={{ backgroundColor: cellIntensity(cost) }}
                          title={cost > 0 ? `$${cost.toLocaleString()}` : t('plan.noCost', 'No cost')}
                        >
                          <span className={cost > 0 && maxCost > 0 && cost / maxCost > 0.5 ? 'text-white' : isAhp ? 'text-gray-600' : 'text-foreground/60'}>
                            {fmtCell(cost)}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Totals row */}
            <tr className={`border-t ${isAhp ? 'border-gray-200' : 'border-foreground/10'}`}>
              <td className={`py-1.5 px-2 font-bold ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
                {t('plan.familyTotal')}
              </td>
              {timelineMonths.map((mInfo) => {
                const total = monthTotals[mInfo.key] || 0;
                return (
                  <td key={mInfo.key} className="py-1.5 px-1 text-center">
                    <span className={`font-mono font-bold ${
                      total > 0
                        ? isAhp ? 'text-[#B22234]' : 'text-matrix-green'
                        : 'text-muted-foreground'
                    }`}>
                      {total > 0 ? (total >= 1000 ? `$${(total / 1000).toFixed(0)}k` : `$${total}`) : '-'}
                    </span>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
